import { Injectable, inject } from '@angular/core';
import { BehaviorSubject, combineLatest, Observable, of } from 'rxjs';
import { concatMap, debounceTime, distinctUntilChanged, filter, map, shareReplay, switchMap } from 'rxjs/operators';

import {
  ManageApplicationModel,
  ManageBorrowerModel,
  ManageBorrowerPayload,
} from '../cmg-1003/components/borrower-information/manage-borrowers-modal/manage-borrower.model';
import { CmgUiUtilities } from '../cmg-1003/components/common/cmg-ui-utils';
import {
  ApplicationInfoModel,
  AssetLiabilitiesModel,
  BorrowerModel,
  Cmg1003SectionTypes,
  LoanPurposeType,
  LoanStatus,
  LockStatusType,
  MortgageType,
  NonPersonEntity,
} from '../cmg-1003/models';
import { FeatureFlagRepository, FeatureName, isLoanSaveAllowed, SessionQuery, softLockStatuses } from '../core/state';
import { LoanApplicationService } from '../disclosure/services/loan-application.service';
import { Form4506CService } from '../form-4506c/form-4506c.service';
import { BusinessChannel, SyncType } from '../models';
import { nonNullish, sortEntitiesByBorrowers } from '../shared/utils';
import { Cmg1003ApplicationInfoQuery } from './application-info/application-info.query';
import { Cmg1003AssetsQuery } from './assetLiabilities/assetLiabilities.query';
import { AusQuery } from './aus/aus.query';
import { Cmg1003BorrowerQuery } from './borrower/borrower.query';
import { Cmg1003DeclarationsQuery } from './declarations/declarations.query';
import { Cmg1003EmploymentIncomeExpensesQuery } from './employment-income-expenses/employment-income-expenses.query';
import { Cmg1003FileDataQuery } from './file-data/file-data.query';
import { Cmg1003GmiQuery } from './gmi/gmi.query';
import { LoanNotesQuery } from './loan-notes/loan-notes.query';
import { Cmg1003LoanOfficerQuery } from './loan-officer/loan-officer.query';
import { Cmg1003LoanQuery } from './loan/loan.query';
import { Cmg1003PropertyQuery } from './property/property.query';
import { Cmg1003TransactionQuery } from './transaction/transaction.query';
import { OccupancyType } from '../scenario-pricing/models';
import { Cmg1003LoanStore } from './loan/loan.store';
import { Cmg1003CoverLetterSubmissionsQuery } from './cover-letter-submissions/cover-letter-submissions.query';
import { Cmg1003LoanStatusHistoryQuery } from './loan-status-history/loan-status-history.query';
import { SecurityProfileQuery } from './security-profile';
import { Cmg1003LookupsService } from '../cmg-1003/services/cmg-1003-lookups.service';
import { Cmg1003FundingReviewQuery } from './funding-review/funding-review.query';
import { Cmg1003ProcessingQuery } from './processing/processing.query';
import { DisclosureApplicationQuery } from '../disclosure/state/disclosure-application/disclosure-application.query';
import { Cmg1003EmploymentExpensesQuery } from './employment-expenses/employment-expenses.query';
import { isNftyDisabledLoanProgram } from '../cmg-1003/utils/is-nfty-loan-program.util';
import { OriginationChannel } from '../disclosure/models/origination-channel.enum';

/**
 * Cmg1003StoreQueryService is a service to create queries when multiple store queries are needed.
 * Advantages of this service:
 * 1) avoids the circular dependency e.g. when appInfo needs borrower and borrower also needs appInfo.
 * 2) rather than importing mutiple query services a component can import only this 1 query service
 * 3) Insulates the component from store changes.
 */

@Injectable({ providedIn: 'root' })
export class Cmg1003StoreQueryService {
  private readonly _appInfoQuery = inject(Cmg1003ApplicationInfoQuery);
  private readonly _assetsQuery = inject(Cmg1003AssetsQuery);
  private readonly _ausQuery = inject(AusQuery);
  private readonly _borrowerQuery = inject(Cmg1003BorrowerQuery);
  private readonly _declarationsQuery = inject(Cmg1003DeclarationsQuery);
  private readonly _disclosuresApplicationService = inject(LoanApplicationService);
  private readonly _employmentExpensesQuery = inject(Cmg1003EmploymentExpensesQuery);
  private readonly _employmentIncomeExpensesQuery = inject(Cmg1003EmploymentIncomeExpensesQuery);
  private readonly _featureFlagRepository = inject(FeatureFlagRepository);
  private readonly _fileDataQuery = inject(Cmg1003FileDataQuery);
  private readonly _form4506CService = inject(Form4506CService);
  private readonly _gmiQuery = inject(Cmg1003GmiQuery);
  private readonly _loanNotesQuery = inject(LoanNotesQuery);
  private readonly _loanOfficerQuery = inject(Cmg1003LoanOfficerQuery);
  private readonly _loanQuery = inject(Cmg1003LoanQuery);
  private readonly _loanStore = inject(Cmg1003LoanStore);
  private readonly _propertyQuery = inject(Cmg1003PropertyQuery);
  private readonly _sessionQuery = inject(SessionQuery);
  private readonly _transactionQuery = inject(Cmg1003TransactionQuery);
  private readonly _coverLetterSubmissionsQuery = inject(Cmg1003CoverLetterSubmissionsQuery);
  private readonly _cmg1003LoanStatusHistoryQuery = inject(Cmg1003LoanStatusHistoryQuery);
  private readonly _securityProfileQuery = inject(SecurityProfileQuery);
  private readonly _lookupsService = inject(Cmg1003LookupsService);
  private readonly _fundingReviewQuery = inject(Cmg1003FundingReviewQuery);
  private readonly _processingQuery = inject(Cmg1003ProcessingQuery);
  private readonly _disclosureAppQuery = inject(DisclosureApplicationQuery);

  private readonly _isStateDirty$ = this._buildIsStateDirtyPipeline(false);
  private readonly _isStateDirtyIgnoreSupplemental$ = this._buildIsStateDirtyPipeline(true);

  isEligibileForPrepaymentPenalty$ = new BehaviorSubject(false);

  constructor() {
    this._featureFlagRepository
      .isFeatureEnabled$(FeatureName.Disclosures)
      .subscribe((isDisclosuresFeatureEnabled) => (this._isDisclosuresFeatureEnabled = isDisclosuresFeatureEnabled));
  }

  private _isDisclosuresFeatureEnabled;

  statusRequiresFullDisabling$ = (ignoreSyncType?: boolean) =>
    combineLatest({
      all1003Loaded: this._sessionQuery.select((s) => s.all1003Loaded),
      loanStatus: this._sessionQuery.select((s) => s.loanStatusId),
      isLockedDown: this._loanQuery.isLockedDown$,
      rateLockStatus: this._sessionQuery.select((s) => s.rateLockStatus),
      syncType: ignoreSyncType ? of(null) : this._fileDataQuery.select((f) => f.syncType),
    }).pipe(
      filter(({ all1003Loaded, loanStatus, syncType }) => {
        return all1003Loaded && loanStatus && (ignoreSyncType || syncType >= 0);
      }),
      debounceTime(100),
      map(({ isLockedDown, loanStatus, rateLockStatus, syncType }) => {
        const statusRequiresFullDisabling = isLockedDown || this._statusRequiresFullDisabling(rateLockStatus, loanStatus);

        // This will be added back when 13503 'Enable/Disable Fields on Loan Summary AND URLA' is redone.
        if (ignoreSyncType) {
          return statusRequiresFullDisabling;
        }

        return isLockedDown || statusRequiresFullDisabling || syncType === SyncType.SecondLien || syncType === SyncType.ThirdLien;
      }),
      distinctUntilChanged(),
    );

  isLockedDown$ = this._loanQuery.isLockedDown$;

  statusRequiresDisabling$ = (ignoreSyncType?: boolean) =>
    combineLatest({
      isLockedDown: this._loanQuery.isLockedDown$,
      all1003Loaded: this._sessionQuery.select((s) => s.all1003Loaded),
      loanStatus: this._sessionQuery.select((s) => s.loanStatusId),
      syncType: ignoreSyncType ? of(null) : this._fileDataQuery.select((f) => f.syncType),
    }).pipe(
      filter(({ all1003Loaded, loanStatus, syncType }) => {
        return all1003Loaded && loanStatus && (ignoreSyncType || syncType >= 0);
      }),
      debounceTime(100),
      map(({ isLockedDown, loanStatus, syncType }) => {
        const statusRequiresDisabling = this._statusRequiresDisabling(loanStatus);

        if (ignoreSyncType) {
          return statusRequiresDisabling;
        }

        return statusRequiresDisabling || isLockedDown || syncType === SyncType.SecondLien || syncType === SyncType.ThirdLien;
      }),
      distinctUntilChanged(),
    );

  /** Checks for these statuses: File Received, or NOT [Locked, Prospect / Credit Only / Pre-Approved, LE Pending / Disclosed]. */
  private readonly _statusRequiresFullDisabling = (rateLockStatus: LockStatusType, loanStatusId: LoanStatus) => {
    return (rateLockStatus === LockStatusType.Locked && loanStatusId !== LoanStatus.LePending) || !softLockStatuses.includes(loanStatusId);
  };

  /** Checks if Loan Status is not a soft lock status requiring partial disabling of certain forms */
  private readonly _statusRequiresDisabling = (loanStatusId: LoanStatus) => {
    return !softLockStatuses.includes(loanStatusId);
  };

  /**
   * Finds entities (e.g. declarations) associated with the selected app's borrower(s).
   * @param query An observable for querying an Akita store. Type is generic but must contain { borrowerId?: number }
   * @param callbackFn Optional. A callback function to manipulate entities as desired.
   * @returns Observable<T[]>. Entites are sorted to have primary borrower FIRST, then additional borrower.
   */
  findEntitiesByCurrentBorrowers<T extends { borrowerId?: number }>(
    query: Observable<T[]>,
    callbackFn?: (queryResult: T[], borrowers: BorrowerModel[]) => T[],
  ) {
    return combineLatest([query, this._appInfoQuery.selectActive()]).pipe(
      distinctUntilChanged(),

      // getBorrowersBySelectedApplicationId() needs to be called in a separate switchMap().
      // If it's within combineLatest(), it returns an outdated list of borrowers.
      switchMap(([queryResult]) => {
        return this.getBorrowersBySelectedApplicationId().pipe(
          map((currentBorrowers) => {
            // Find the entities (from query) associated with the app's borrowerIds.
            let matchingEntities = queryResult.filter((qr) => currentBorrowers?.map((cb) => cb.borrowerId).includes(qr?.borrowerId));
            matchingEntities = sortEntitiesByBorrowers(matchingEntities, currentBorrowers);

            // Terminate if no matches.
            if (!matchingEntities.length) {
              return;
            }

            // Apply the callback function, if provided.
            if (callbackFn) {
              matchingEntities = callbackFn(matchingEntities, currentBorrowers);
            }

            return matchingEntities;
          }),
        );
      }),
    );
  }

  /**
   * @param forBorrowerComponent Should only be true when called from borrower-information template
   * @returns A list of borrower ids
   */
  getBorrowersBySelectedApplicationId(forBorrowerComponent = false) {
    return this._appInfoQuery
      .selectActive()
      .pipe(
        switchMap((app) => {
          if (!app) {
            return [];
          }
          if (forBorrowerComponent) {
            return of([app.borrowerId, app.coBorrowerId]);
          }
          const validBorrowersList = [app.borrowerId]; // primary borrower is always there
          if (!CmgUiUtilities.isIdTemporary(app.coBorrowerId) || (CmgUiUtilities.isIdTemporary(app.coBorrowerId) && app?.additionalBorrowerChecked)) {
            validBorrowersList.push(app.coBorrowerId);
          }
          // For every section other than Borrower Info
          return of(validBorrowersList);
        }),
      )
      .pipe(switchMap((borrowerIds) => this._borrowerQuery.selectMany(borrowerIds)));
  }

  /**
   * Bypass function to avoid importing ApplicationInfoQuery
   * @returns
   */
  getActiveApplication() {
    return this._appInfoQuery.selectActive();
  }

  getActiveApplicationId() {
    return +this._appInfoQuery.getActiveId();
  }

  getAllApplicationsWithStatus() {
    const activeApplicationId = +this._appInfoQuery.getActiveId();
    return this._appInfoQuery
      .getAll()
      .map((t) => t.applicationId)
      .map((t) => {
        return {
          applicationId: t,
          isActive: t === activeApplicationId,
        };
      });
  }

  getApplicationById(applicationId: number) {
    return this._appInfoQuery.getEntity(applicationId);
  }

  getAllBorrowerIdsFromCurrentApplications(applicationId: number): number[] {
    const activeApp = this._appInfoQuery.getEntity(applicationId);
    const borrowerIds = this._appInfoQuery.getAll().map((application) => application.borrowerId);
    if (
      !CmgUiUtilities.isIdTemporary(activeApp.coBorrowerId) ||
      (CmgUiUtilities.isIdTemporary(activeApp.coBorrowerId) && activeApp.additionalBorrowerChecked)
    ) {
      borrowerIds.push(activeApp.coBorrowerId);
    }

    return borrowerIds;
  }

  getAllApplications(): ApplicationInfoModel[] {
    return this._appInfoQuery.getAll();
  }

  getBorrowersByApplicationId(applicationId: number, forAutoRecovery = false) {
    const activeApp = this._appInfoQuery.getEntity(applicationId);
    const borrowers = [activeApp?.borrowerId];
    if (
      !CmgUiUtilities.isIdTemporary(activeApp?.coBorrowerId) ||
      (CmgUiUtilities.isIdTemporary(activeApp?.coBorrowerId) && activeApp?.additionalBorrowerChecked) ||
      forAutoRecovery
    ) {
      borrowers.push(activeApp?.coBorrowerId);
    }
    return borrowers;
  }

  /** Returns the 1003 sections that are marked dirty. */
  getDirtySections() {
    const dirtySections: string[] = [
      { storeName: CmgUiUtilities.getSectionName(Cmg1003SectionTypes.loanOfficer), isDirty: this._loanOfficerQuery.dirtyChecker2.isDirty() },
      { storeName: CmgUiUtilities.getSectionName(Cmg1003SectionTypes.borrowerInformation), isDirty: this._borrowerQuery.dirtyChecker2.isDirty() },
      { storeName: CmgUiUtilities.getSectionName(Cmg1003SectionTypes.loanInformation), isDirty: this._loanQuery.dirtyChecker2.isDirty() },
      {
        storeName: CmgUiUtilities.getSectionName(Cmg1003SectionTypes.propertyInformation),
        isDirty:
          this._propertyQuery.dirtyChecker2.isDirty() ||
          this._fileDataQuery.occupancyTypeDirtyChecker.isDirty() ||
          this._loanQuery.loanPurposeDirtyChecker.isDirty(),
      },
      {
        storeName: CmgUiUtilities.getSectionName(Cmg1003SectionTypes.employmentInformation),
        isDirty: this._employmentIncomeExpensesQuery.dirtyChecker2.isDirty(),
      },
      {
        storeName: CmgUiUtilities.getSectionName(Cmg1003SectionTypes.assetsCreditLiabilitiesInformation),
        isDirty: this._assetsQuery.dirtyChecker2.isDirty(),
      },
      { storeName: CmgUiUtilities.getSectionName(Cmg1003SectionTypes.transactionDetails), isDirty: this._transactionQuery.dirtyChecker2.isDirty() },

      { storeName: CmgUiUtilities.getSectionName(Cmg1003SectionTypes.declarations), isDirty: this._declarationsQuery.dirtyChecker2.isDirty() },
      {
        storeName: CmgUiUtilities.getSectionName(Cmg1003SectionTypes.gmiAndMilitaryServiceInformation),
        isDirty: this._gmiQuery.dirtyChecker2.isDirty(),
      },
      {
        storeName: CmgUiUtilities.getSectionName(Cmg1003SectionTypes.continuationSheet),
        isDirty: false, // Continuation Sheet is always disabled
      },
    ]
      .filter((section) => section.isDirty)
      .map((section) => section.storeName);

    return dirtySections;
  }

  isStateDirty$(ignoreSupplemental = false): Observable<boolean> {
    return ignoreSupplemental ? this._isStateDirtyIgnoreSupplemental$ : this._isStateDirty$;
  }

  private _buildIsStateDirtyPipeline(ignoreSupplemental: boolean): Observable<boolean> {
    const isForm4506CDirty$ = this._form4506CService.update$.pipe(map((value) => nonNullish(value)));

    const isDisclosuresDirty$ = combineLatest([
      this._featureFlagRepository.isFeatureEnabled$(FeatureName.Disclosures),
      ignoreSupplemental ? this._disclosuresApplicationService.isDirtyWithoutSupplemental$ : this._disclosuresApplicationService.isDirty$,
    ]).pipe(map(([isFeatureEnabled, isDirty]) => isFeatureEnabled && isDirty));

    return combineLatest([
      this._assetsQuery.dirtyChecker2.isDirty$,
      this._borrowerQuery.dirtyChecker2.isDirty$,
      this._declarationsQuery.dirtyChecker2.isDirty$,
      this._employmentIncomeExpensesQuery.dirtyChecker2.isDirty$,
      this._fileDataQuery.dirtyChecker2.isDirty$,
      this._gmiQuery.dirtyChecker2.isDirty$,
      this._loanOfficerQuery.dirtyChecker2.isDirty$,
      this._propertyQuery.dirtyChecker2.isDirty$,
      this._processingQuery.dirtyChecker.isDirty$,
      this._transactionQuery.dirtyChecker2.isDirty$,
      this._loanQuery.dirtyChecker2.isDirty$,
      this._ausQuery.dirtyChecker2.isDirty$,
      isForm4506CDirty$,
      isDisclosuresDirty$,
      this._sessionQuery.isLoanSaveAllowed$,
      this._coverLetterSubmissionsQuery.dirtyChecker.isDirty$,
      this._disclosureAppQuery.dirtyChecker.isDirty$,
    ]).pipe(
      debounceTime(50),
      map(
        ([
          assetsDirty,
          borrowersDirty,
          declarationsDirty,
          employmentIncomeExpensesDirty,
          fileDataDirty,
          gmiDirty,
          loanOfficerDirty,
          propertyDirty,
          processingDirty,
          transactionDirty,
          loanDirty,
          ausDirty,
          form4506CDirty,
          disclosuresDirty,
          isLoanSaveAllowed,
          isCoverLetterSubmissionsDirty,
          isDisclosureApplicationsDirty,
        ]) => {
          const isStateDirty =
            (assetsDirty ||
              borrowersDirty ||
              declarationsDirty ||
              employmentIncomeExpensesDirty ||
              fileDataDirty ||
              gmiDirty ||
              loanOfficerDirty ||
              processingDirty ||
              propertyDirty ||
              transactionDirty ||
              loanDirty ||
              ausDirty ||
              form4506CDirty ||
              disclosuresDirty ||
              isCoverLetterSubmissionsDirty ||
              isDisclosureApplicationsDirty) &&
            isLoanSaveAllowed === true;

          return isStateDirty;
        },
      ),
      distinctUntilChanged(),
      shareReplay(1),
    );
  }

  isURLADirty() {
    const dirty =
      (this._assetsQuery.dirtyChecker2.isDirty() ||
        // this._borrowerQuery.dirtyChecker.isDirty() ||
        this._borrowerQuery.dirtyChecker2.isDirty() ||
        this._declarationsQuery.dirtyChecker2.isDirty() ||
        this._employmentIncomeExpensesQuery.dirtyChecker2.isDirty() ||
        this._fileDataQuery.dirtyChecker2.isDirty() ||
        this._gmiQuery.dirtyChecker2.isDirty() ||
        this._loanOfficerQuery.dirtyChecker2.isDirty() ||
        this._propertyQuery.dirtyChecker2.isDirty() ||
        this._transactionQuery.dirtyChecker2.isDirty() ||
        this._loanQuery.dirtyChecker2.isDirty() ||
        this._ausQuery.dirtyChecker2.isDirty() ||
        (this._isDisclosuresFeatureEnabled ? this._disclosuresApplicationService.isDirty() : false)) &&
      isLoanSaveAllowed(this._sessionQuery.getValue()?.loanStatusId);

    return dirty;
  }

  isStateDirty() {
    const dirty =
      (this._assetsQuery.dirtyChecker2.isDirty() ||
        this._borrowerQuery.dirtyChecker2.isDirty() ||
        this._declarationsQuery.dirtyChecker2.isDirty() ||
        this._employmentIncomeExpensesQuery.dirtyChecker2.isDirty() ||
        this._fileDataQuery.dirtyChecker2.isDirty() ||
        this._gmiQuery.dirtyChecker2.isDirty() ||
        this._loanOfficerQuery.dirtyChecker2.isDirty() ||
        this._propertyQuery.dirtyChecker2.isDirty() ||
        this._transactionQuery.dirtyChecker2.isDirty() ||
        this._loanQuery.dirtyChecker2.isDirty() ||
        this._ausQuery.dirtyChecker2.isDirty() ||
        this._coverLetterSubmissionsQuery.dirtyChecker.isDirty() ||
        (this._isDisclosuresFeatureEnabled ? this._disclosuresApplicationService.isDirty() : false)) &&
      isLoanSaveAllowed(this._sessionQuery.getValue()?.loanStatusId);

    return dirty;
  }

  setHead() {
    this._appInfoQuery.dirtyChecker.setHead();
    this._assetsQuery.dirtyChecker2.dirtyChecker.setHead();
    this._borrowerQuery.dirtyChecker2.dirtyChecker.setHead();
    this._declarationsQuery.dirtyChecker2.dirtyChecker.setHead();
    this._employmentExpensesQuery.dirtyChecker2.dirtyChecker.setHead();
    this._employmentIncomeExpensesQuery.dirtyChecker2.dirtyChecker.setHead();
    this._fileDataQuery.dirtyChecker2.dirtyChecker.setHead();
    this._gmiQuery.dirtyChecker2.dirtyChecker.setHead();
    this._loanOfficerQuery.dirtyChecker2.dirtyChecker.setHead();
    this._propertyQuery.dirtyChecker2.dirtyChecker.setHead();
    this._transactionQuery.dirtyChecker2.dirtyChecker.setHead();
    this._loanQuery.setHeadAllDirtyCheckers();
    this._ausQuery.dirtyChecker2.dirtyChecker.setHead();
    this._loanNotesQuery.dirtyChecker2.dirtyChecker.setHead();
    this._coverLetterSubmissionsQuery.dirtyChecker.setHead();
    this._fundingReviewQuery.dirtyChecker.setHead();
    this._processingQuery.dirtyChecker.setHead();

    // Akita watchProperty - For auto-recovery change tracking.
    this._fileDataQuery.occupancyTypeDirtyChecker.setHead();
    this._cmg1003LoanStatusHistoryQuery.dirtyChecker.setHead();
  }

  rollback() {
    this._appInfoQuery.dirtyChecker.reset();
    this._assetsQuery.dirtyChecker2.dirtyChecker.reset();
    this._borrowerQuery.dirtyChecker2.dirtyChecker.reset();
    this._declarationsQuery.dirtyChecker2.dirtyChecker.reset();
    this._employmentIncomeExpensesQuery.dirtyChecker2.dirtyChecker.reset();
    this._fileDataQuery.dirtyChecker2.dirtyChecker.reset();
    this._gmiQuery.dirtyChecker2.dirtyChecker.reset();
    this._loanOfficerQuery.dirtyChecker2.dirtyChecker.reset();
    this._propertyQuery.dirtyChecker2.dirtyChecker.reset();
    this._transactionQuery.dirtyChecker2.dirtyChecker.reset();
    this._loanQuery.resetAllDirtyCheckers();
    this._ausQuery.dirtyChecker2.dirtyChecker.reset();
    this._coverLetterSubmissionsQuery.dirtyChecker.reset();
    this._fundingReviewQuery.dirtyChecker.reset();
    this._processingQuery.dirtyChecker.reset();

    // Akita watchProperty - For auto-recovery change tracking.
    this._fileDataQuery.occupancyTypeDirtyChecker.reset();
    // Reset disclosures form
    this._disclosuresApplicationService.reset();
    this._cmg1003LoanStatusHistoryQuery.dirtyChecker.reset();
    this._disclosureAppQuery.dirtyChecker.reset();
  }

  /**
   * Retrieves all applications and their associated borrowers for managing borrower assignments.
   *
   * @returns An Observable that emits a ManageBorrowerPayload containing:
   *   - applications: Array of ManageApplicationModel with display order and deletion status
   *   - borrowers: Array of ManageBorrowerModel with borrower details, their assigned applications, and primary status
   *
   * This implementation uses a reactive approach where:
   * 1. First, we retrieve all applications sorted by display order
   * 2. Then, for each application, we create two observable queries:
   *    - One for the primary borrower
   *    - One for the co-borrower (if applicable)
   * 3. We use combineLatest to combine all borrower observables, which will reactively update
   *    whenever any of the underlying borrower data changes
   * 4. Finally, we filter out any null borrowers and return the complete payload
   */
  selectApplicationsAndBorrowers(): Observable<ManageBorrowerPayload> {
    // Get all applications sorted by display order
    const applications$ = this._appInfoQuery.selectAll({ sortBy: (a) => a.displayOrder }).pipe(
      map((apps) => ({
        apps,
        applications: apps.map(
          (app, index) =>
            ({
              id: app.applicationId,
              isDeleted: false,
              displayOrder: index,
            }) as ManageApplicationModel,
        ),
      })),
    );

    // Combine with borrowers data
    return applications$.pipe(
      switchMap(({ apps, applications }) => {
        // If no applications, return empty payload
        if (!apps.length) {
          return of({ applications, borrowers: [] });
        }

        // Create borrower selector queries for all borrowers
        const borrowerQueries$ = apps.flatMap((app, appIndex) => [
          // Primary borrower query
          this._borrowerQuery
            .selectEntity((t) => t.borrowerId === app.borrowerId)
            .pipe(
              map((borrower) => {
                if (!borrower) {
                  return null;
                }
                let borrowerName = borrower.firstName + ' ' + borrower.lastName;
                borrowerName = !borrowerName || !borrowerName?.trim() ? 'New Borrower' : borrowerName;

                return {
                  id: borrower.borrowerId,
                  name: borrowerName,
                  email: borrower.email,
                  appId: borrower.applicationId,
                  displayOrder: 0,
                  isPrimary: appIndex === 0,
                  originalAppId: borrower.applicationId,
                  originalDisplayOrder: 0,
                } as ManageBorrowerModel;
              }),
            ),

          // Co-borrower query
          this._borrowerQuery
            .selectEntity((t) => t.borrowerId === app.coBorrowerId)
            .pipe(
              map((borrower) => {
                if (!borrower || borrower.borrowerId <= 0) {
                  return null;
                }
                let borrowerName = borrower.firstName + ' ' + borrower.lastName;
                borrowerName = !borrowerName || !borrowerName?.trim() ? 'New Borrower' : borrowerName;

                return {
                  id: borrower.borrowerId,
                  name: borrowerName,
                  email: borrower.email,
                  appId: borrower.applicationId,
                  displayOrder: 1,
                  isPrimary: false,
                  originalAppId: borrower.applicationId,
                  originalDisplayOrder: 1,
                } as ManageBorrowerModel;
              }),
            ),
        ]);

        // Combine all borrower results
        return combineLatest(borrowerQueries$).pipe(
          map((borrowerResults) => {
            const borrowers = borrowerResults.filter((borrower) => !!borrower) as ManageBorrowerModel[];
            return { borrowers, applications } as ManageBorrowerPayload;
          }),
        );
      }),
    );
  }

  selectTotalLiabilitiesAndTotalPayments$(): Observable<TotalLiabilitiesAndPayments[]> {
    return this._assetsQuery.selectAll().pipe(
      map((assets: AssetLiabilitiesModel[]) => {
        const totals: TotalLiabilitiesAndPayments[] = [];
        // Total number of application displayed should be no more than 4
        // That's why i < 4 is here
        for (let i = 0; i <= assets.length && i < 4; i++) {
          if (assets[i] !== undefined && assets[i] !== null) {
            const total: TotalLiabilitiesAndPayments = {
              totalLiabilities: this._assetsQuery.calculateTotalLiabilities(assets[i]?.liabilitiesList),
              totalPayments: this._assetsQuery.calculateTotalMonthlyPayments(assets[i]?.liabilitiesList),
            };
            totals.push(total);
          }
        }
        return totals;
      }),
    );
  }

  /**
   * Will clear out Prepayment Penalty values if/when the loan becomes ineligible to have a Prepayment Penalty,
   * and sends out updated eligibility value on isEligibileForPrepaymentPenalty$.
   */
  handlePrepaymentPenaltyEligibility() {
    combineLatest({
      all1003Loaded: this._sessionQuery.select((s) => s.all1003Loaded),
      businessChannel: this._securityProfileQuery.selectActive((s) => s.businessChannel),
      originationChannel: this._loanQuery.select((l) => l.originationChannel),
      loanPurpose: this._loanQuery.select((l) => l.loanPurpose),
      mortgageAppliedFor: this._loanQuery.select((l) => l.mortgageAppliedFor),
      loanProgramCode: this._loanQuery.select((l) => l.loanProgramCode),
      loanProgramName: this._loanQuery.select((l) => l.loanProgramName),
      totalLoanAmount: this._loanQuery.select((l) => l.totalLoanAmount),
      occupancyType: this._fileDataQuery.select((f) => f.occupancyType),
      isBusinessPurpose: this._fileDataQuery.select((f) => f.isBusinessPurpose),
      state: this._propertyQuery.select((p) => p.state),
      city: this._propertyQuery.select((p) => p.city),
      county: this._propertyQuery.select((p) => p.county),
      borrowers: this._borrowerQuery.selectAll(),
      apr: this._loanQuery.select((l) => l.apr),
    })
      .pipe(
        filter(({ all1003Loaded }) => all1003Loaded),
        distinctUntilChanged(),
      )
      .subscribe(
        ({
          businessChannel,
          originationChannel,
          loanPurpose,
          loanProgramCode,
          loanProgramName,
          mortgageAppliedFor,
          totalLoanAmount,
          occupancyType,
          isBusinessPurpose,
          state,
          city,
          county,
          borrowers,
          apr,
        }) => {
          let canShowPrepaymentPenaltyFields = false;
          const nonPersonInfo = borrowers?.find((b) => b.isPrimaryBorrower)?.nonPersonInformation;

          if (
            originationChannel === OriginationChannel.retail &&
            !loanProgramCode.toLowerCase().includes('2nd') &&
            isBusinessPurpose &&
            occupancyType === OccupancyType.InvestmentProperty &&
            (businessChannel === BusinessChannel.Retail || businessChannel === BusinessChannel.JV)
          ) {
            if (loanProgramCode?.toLowerCase().includes('connect')) {
              const statesToRestrictPrepaymentPenalty = ['AK', 'DE', 'IA', 'HI', 'MI', 'MN', 'MS', 'NJ', 'NH', 'NM', 'OH', 'RI', 'WV', 'VT'];
              canShowPrepaymentPenaltyFields =
                (loanPurpose === LoanPurposeType.purchase || loanPurpose === LoanPurposeType.refinance) &&
                mortgageAppliedFor === MortgageType.conventional &&
                !statesToRestrictPrepaymentPenalty.includes(state);
            } else if (
              loanProgramCode?.toLowerCase().includes('nqm edge') ||
              loanProgramCode?.toLowerCase().includes('nqm sharp') ||
              loanProgramCode?.toLowerCase().includes('nqm advantage') ||
              loanProgramCode?.toLowerCase().includes('nqm horizon') ||
              loanProgramCode?.toLowerCase().includes('nqm access') ||
              loanProgramCode?.toLowerCase().includes('nqm river') ||
              loanProgramName?.toLowerCase().includes('non-qm')
            ) {
              const statesToRestrictPrepaymentPenalty = [
                'AK',
                'AR',
                'DE',
                'IL',
                'KS',
                'KY',
                'LA',
                'MD',
                'MI',
                'MN',
                'MS',
                'NJ',
                'NY',
                'NM',
                'OH',
                'PA',
                'RI',
                'VT',
                'NC',
              ];

              canShowPrepaymentPenaltyFields =
                !statesToRestrictPrepaymentPenalty.includes(state) ||
                (state === 'PA' && totalLoanAmount >= 319777) ||
                (state === 'VT' && totalLoanAmount >= 1000000) ||
                (state === 'IL' && (county === 'Cook' || city === 'Chicago') && totalLoanAmount > 250000) ||
                (state === 'IL' && nonPersonInfo?.isNonPerson && nonPersonInfo?.entityType === NonPersonEntity.LimitedLiabilityCompany) ||
                (state === 'IL' && apr <= 8) ||
                (state === 'NC' && totalLoanAmount > 100000);
            }
          }

          if (!canShowPrepaymentPenaltyFields) {
            this._loanStore.update({ hasPrepaymentPenalty: false, isPrepaymentPenalty: false, prepaymentPenaltyMax: 0, prepaymentPenaltyTerm: null });
          }

          this.isEligibileForPrepaymentPenalty$.next(canShowPrepaymentPenaltyFields);
        },
      );
  }

  // Sandbox Loan determined by a Loan Status of 76 - Sandbox
  isSandboxLoan$: Observable<boolean> = this._sessionQuery.select((state) => state.loanStatusId === LoanStatus.Sandbox);

  //Disable all pages and form fields except Copy Loan
  get isClear2DisabledForLoan$(): Observable<boolean> {
    return combineLatest({
      isNFTLoan: this._loanQuery.select((loan) => {
        return isNftyDisabledLoanProgram(loan?.loanProgramCode);
      }),
      all1003Loaded: this._sessionQuery.select((s) => s.all1003Loaded),
    }).pipe(
      filter(({ all1003Loaded }) => all1003Loaded),
      concatMap(({ isNFTLoan }) => {
        return of(isNFTLoan ?? false);
      }),
    );
  }
}

type TotalLiabilitiesAndPayments = {
  totalLiabilities: number;
  totalPayments: number;
};
